import { useEffect, useState } from "react";
import { loadPlacesUiKit } from "../googleMaps";

interface Props {
  placeId: string | null | undefined;
  apiKey: string | null;
}

/**
 * Google's rating, rendered by the Places UI Kit.
 *
 * Two constraints shape this component, both from the Maps Platform terms:
 *   - the rating VALUE never passes through our API, so it is fetched here and
 *     nowhere else (docs/RATINGS.md);
 *   - Places UI Kit is the one Google surface permitted alongside a non-Google map,
 *     which is what we render. Attribution comes with the element.
 *
 * It renders nothing at all when there is no key or no resolved place ID - ratings
 * are enrichment, and their absence must never look like an error.
 */
export function RatingCard({ placeId, apiKey }: Props) {
  const [state, setState] = useState<"idle" | "ready" | "failed">("idle");

  useEffect(() => {
    if (!apiKey || !placeId) return;
    let cancelled = false;
    loadPlacesUiKit(apiKey)
      .then(() => !cancelled && setState("ready"))
      .catch(() => !cancelled && setState("failed"));
    return () => {
      cancelled = true;
    };
  }, [apiKey, placeId]);

  if (!apiKey || !placeId) return null;
  if (state === "failed") return null;
  if (state === "idle") return <p className="muted small">Loading rating…</p>;

  return (
    <div className="rating-card">
      <gmp-place-details-compact orientation="horizontal" truncation-preferred>
        <gmp-place-details-place-request place={placeId} />
        <gmp-place-all-content />
      </gmp-place-details-compact>
    </div>
  );
}
